import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import React from "react";
import { StatisticsData } from "../../../models/analytics";

interface StatisticsRow {
  label: string;
  value?: number | string;
  percent?: number;
  sd?: number;
  level: number;
}

const transformBackendData = (data: StatisticsData): StatisticsRow[] => {
  const rows: StatisticsRow[] = [];

  rows.push({ label: "Demographics", level: 0 });
  rows.push({
    label: "Male",
    value: data.demographics.male,
    percent: +((data.demographics.male / data.total_patients) * 100).toFixed(1),
    level: 1,
  });
  rows.push({
    label: "Age, years (mean [SD])",
    value: data.demographics.age_mean,
    sd: data.demographics.age_sd,
    level: 1,
  });

  rows.push({ label: "Age Groups", level: 1 });
  Object.entries(data.demographics.age_groups).forEach(([group, count]) => {
    rows.push({
      label: group,
      value: count,
      percent: +((count / data.total_patients) * 100).toFixed(1),
      level: 2,
    });
  });
  rows.push({
    label: "Follow-up duration, months (mean [SD])",
    value: data.demographics.follow_up_duration_mean,
    sd: data.demographics.follow_up_duration_sd,
    level: 1,
  });

  rows.push({ label: "Metastasis at baseline", level: 0 });
  rows.push({
    label: "Presence",
    value: data.metastasis_at_baseline.presence,
    percent: +((data.metastasis_at_baseline.presence / data.total_patients) * 100).toFixed(1),
    level: 1,
  });

  rows.push({ label: "Locations", level: 1 });
  Object.entries(data.metastasis_at_baseline.locations)
    .sort(([, a], [, b]) => b - a)
    .forEach(([location, count]) => {
      rows.push({
        label: location,
        value: count,
        percent: +((count / data.total_patients) * 100).toFixed(1),
        level: 2,
      });
    });

  rows.push({ label: "Comorbidities at baseline", level: 0 });
  Object.entries(data.comorbidities_at_baseline.types)
    .sort(([, a], [, b]) => b - a)
    .forEach(([comorbidity, count]) => {
      rows.push({
        label: comorbidity,
        value: count,
        percent: +((count / data.total_patients) * 100).toFixed(1),
        level: 1,
      });
    });

  return rows;
};

interface StatisticsTableProps {
  data: StatisticsData;
}

const StatisticsTable: React.FC<StatisticsTableProps> = ({ data }) => {
  const rows = React.useMemo(() => transformBackendData(data), [data]);

  return (
    <Table
      aria-label="cohort statistics"
      size="sm"
      sx={{ "& tr > *:not(:first-child)": { textAlign: "right" } }}
      hoverRow
    >
      <thead>
        <tr>
          <th rowSpan={2} scope="row">
            Characteristics
          </th>
          <th
            colSpan={2}
            style={{
              textAlign: "right",
              width: "30%",
              borderBottomWidth: "2px",
            }}
          >
            Total (n={data.total_patients})
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${row.label}-${row.level}-${index}`}>
            <td style={{ paddingLeft: `${10 + row.level * 20}px` }}>
              {row.level === 0 ? <Typography fontWeight="bold">{row.label}</Typography> : row.label}
            </td>
            <td>{row.value !== undefined ? row.value : ""}</td>
            <td style={{ borderLeftWidth: 0 }}>
              {(() => {
                if (row.percent !== undefined) {
                  return `(${row.percent}%)`;
                }
                if (row.sd !== undefined) {
                  return `(σ = ${row.sd})`;
                }
                return "";
              })()}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default StatisticsTable;
